import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";
import { SearchInput } from "../../../design-system";
import { useEdit } from "../../context/EditContext";
import {
  fetchElementStylePresetPreviews,
  SUPPORTED_ELEMENT_STYLE_PRESET_TYPES,
} from "./elementStylePresetsApi";
import {
  buildPresetFamilies,
  ElementStylePresetFamilyCard,
} from "./ElementStylePresetFamilyCard";

function normalizeSearchTerm(raw) {
  return typeof raw === "string" ? raw.trim().toLowerCase() : "";
}

function presetMatchesSearch(preset, term) {
  if (!term) return true;
  const name = normalizeSearchTerm(preset?.name);
  const value = normalizeSearchTerm(preset?.value);
  return name.includes(term) || value.includes(term);
}

export default function ElementStylePresetsTab() {
  const { selectedElement, applyStylePreset } = useEdit();
  const [applyingPreset, setApplyingPreset] = useState(null);
  const [applyError, setApplyError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const elementType = selectedElement?.type || null;
  const isSupported = SUPPORTED_ELEMENT_STYLE_PRESET_TYPES.has(elementType);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["element-style-presets", elementType],
    queryFn: () => fetchElementStylePresetPreviews(elementType),
    enabled: Boolean(elementType && isSupported),
    staleTime: 5 * 60 * 1000,
  });

  const presets = Array.isArray(data?.presets) ? data.presets : [];
  const normalizedQuery = normalizeSearchTerm(searchQuery);

  const families = useMemo(() => buildPresetFamilies(presets), [presets]);

  const filteredFamilies = useMemo(() => {
    if (!normalizedQuery) return families;
    return families.filter((family) =>
      family.presets.some((p) => presetMatchesSearch(p, normalizedQuery)),
    );
  }, [families, normalizedQuery]);

  const handleApplyPreset = async (presetValue) => {
    if (!presetValue || !selectedElement || !applyStylePreset) return;

    try {
      setApplyError(null);
      setApplyingPreset(presetValue);
      await applyStylePreset(presetValue);
    } catch (applyErr) {
      setApplyError(applyErr?.message || "Failed to apply style preset.");
    } finally {
      setApplyingPreset(null);
    }
  };

  if (!isSupported) {
    return (
      <div className={`rounded-lg border ${borders.default} ${surfaces.secondary} p-4`}>
        <div className={`${typography.bodySmall} ${textColors.muted}`}>
          Style presets are not available for this element.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className={`${typography.h4} ${textColors.primary}`}>Style Change</h3>

      {!isLoading && !error && presets.length > 0 ? (
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery("")}
          placeholder="Search styles..."
          size="sm"
        />
      ) : null}

      {applyError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          {applyError}
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          <span className={`ml-3 ${typography.bodySmall} ${textColors.primary}`}>
            Loading preset previews...
          </span>
        </div>
      ) : null}

      {!isLoading && error ? (
        <div className={`rounded-lg border ${borders.default} ${surfaces.primary} p-4`}>
          <div className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
            {error?.message || "Failed to load preset previews."}
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
          >
            Try again
          </button>
        </div>
      ) : null}

      {!isLoading && !error && presets.length === 0 ? (
        <div className={`rounded-lg border ${borders.default} ${surfaces.primary} p-4`}>
          <div className={`${typography.bodySmall} ${textColors.muted}`}>
            No preset previews are available for this element yet.
          </div>
        </div>
      ) : null}

      {!isLoading && !error && presets.length > 0 && filteredFamilies.length === 0 ? (
        <div className={`rounded-lg border ${borders.default} ${surfaces.primary} p-4`}>
          <div className={`${typography.bodySmall} ${textColors.muted}`}>
            No styles match "{searchQuery}"
          </div>
        </div>
      ) : null}

      {!isLoading && !error && filteredFamilies.length > 0 ? (
        <div className="space-y-5">
          {filteredFamilies.map((family) => (
            <ElementStylePresetFamilyCard
              key={family.key}
              family={family}
              elementType={elementType}
              applyingPreset={applyingPreset}
              onApply={handleApplyPreset}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
